import React, { useCallback, useMemo } from 'react';
import ReactSelect from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import cn from 'classnames';
import _get from 'lodash/get';
import _size from 'lodash/size';
import _isEmpty from 'lodash/isEmpty';
import _toLower from 'lodash/toLower';
import styles from './index.module.scss';

import { UiKit } from 'components';
import {
  SelectOption,
  SelectMultiValue,
  SelectValueContainer,
  SelectClearIndicator,
  SelectDropdownIndicator
} from './components';
import { selectHelpers } from 'utils/uiKitHelpers';
import { formatGroupLabel, getCustomSelectStyles } from './utils';
import { SELECT_ALL_OPTION_ID } from './stubs';

/**
 * @param {{
 *  label?: string
 *  caption?: string
 *  error?: string
 *  isAsync?: boolean
 *  options?: import("utils/types").SelectOption[]
 *  value?: import("utils/types").SelectOption | import("utils/types").SelectOption[]
 *  onChange?: (value: import("utils/types").SelectOption | import("utils/types").SelectOption[]) => void
 *  isMulti?: boolean
 *  isNested?: boolean
 *  required?: boolean
 *  isLoading?: boolean
 *  isDisabled?: boolean
 *  isCreatable?: boolean
 *  isSearchable?: boolean,
 *  menuPosition?: 'absolute' | 'fixed' | 'relative',
 *  hideSelectAllOption?: boolean
 *  selectAllOptionsLabel?: string
 *  noOptionsMessage? () => string
 *  components: (typeof import("react-select/src/components").components)
 *  loadOptions: (inputValue: string, callback: (options: import("utils/types").SelectOption[]) => {}) => Promise<void>
 *  className?: string
 *  labelClassName?: string
 *  inputClassName?: string
 * }} props
 */
export default function Select({
  label = '',
  caption = '',
  error = '',
  isAsync = false,
  options = [],
  value = null,
  onChange = () => {},
  components = {},
  isMulti = false,
  isNested = false,
  required = false,
  isLoading = false,
  isDisabled = false,
  isCreatable = false,
  isSearchable = true,
  hideSelectAllOption = false,
  selectAllOptionsLabel = 'Select All',
  noOptionsMessage = () => 'No options available',
  className = '',
  labelClassName = '',
  inputClassName = '',
  ...reactSelectProps
}) {
  const HybridSelect = useMemo(() => {
    return isCreatable ? CreatableSelect : isAsync ? AsyncSelect : ReactSelect;
  }, [isAsync, isCreatable]);

  const { selectOptions, allOptions } = useMemo(() => {
    let selectOptions = options;
    if (isMulti && !hideSelectAllOption && !_isEmpty(selectOptions)) {
      selectOptions = [
        { label: selectAllOptionsLabel, value: SELECT_ALL_OPTION_ID },
        ...selectOptions
      ];
    }
    const allOptions = selectHelpers.createAllOptions(selectOptions);
    return { selectOptions, allOptions };
  }, [options, isMulti, hideSelectAllOption, selectAllOptionsLabel]);

  const onFilterOption = useCallback(
    (option, inputValue) => {
      const queryKey = isNested ? 'data.query' : 'label';
      const query = _get(option, queryKey, '');
      return _toLower(query).includes(_toLower(inputValue));
    },
    [isNested]
  );

  const onSelect = option => {
    if (!isMulti) return onChange(option);

    const newOption = option[_size(option) - 1];
    const newOptionId = _get(newOption, 'value', '');
    const isAllOptionsId = newOptionId === SELECT_ALL_OPTION_ID;

    if (!isAllOptionsId) return onChange(option);

    const allValidOptions = allOptions.slice(1); // discarding the "Select All" option
    const areAllSelected = _size(value) === _size(allValidOptions);
    onChange(areAllSelected ? [] : allValidOptions);
  };

  return (
    <div className={cn(styles.container, className)}>
      <UiKit.Label text={label} className={labelClassName}>
        <HybridSelect
          isMulti={isMulti}
          value={value}
          options={isNested ? allOptions : selectOptions}
          onChange={onSelect}
          required={required}
          isLoading={isLoading}
          isDisabled={isLoading || isDisabled}
          // isOptionDisabled={() => isLoading || isDisabled}  // React-Select seems buggy with this prop
          isSearchable={isSearchable}
          hideSelectedOptions={false}
          tabSelectsValue={false}
          backspaceRemovesValue={false}
          closeMenuOnSelect={!isMulti}
          filterOption={onFilterOption}
          formatGroupLabel={formatGroupLabel}
          noOptionsMessage={noOptionsMessage}
          className={inputClassName}
          styles={getCustomSelectStyles({ error })}
          components={{
            Option: SelectOption,
            MultiValue: SelectMultiValue,
            ValueContainer: SelectValueContainer,
            ClearIndicator: SelectClearIndicator,
            DropdownIndicator: SelectDropdownIndicator,
            ...components
          }}
          {...reactSelectProps}
          // 👇 Custom Props ----------------------------------------
          isNested={isNested}
          allOptions={allOptions}
          // --------------------------------------------------------
        />
      </UiKit.Label>
      <UiKit.Error msg={error} hide={isDisabled} />
      <UiKit.Caption>{caption}</UiKit.Caption>
    </div>
  );
}
